import { useState, useEffect, useRef } from "react";
import { SoloStreamData } from "../types";
import {
  streamService,
  PublicStreamData,
} from "../../../../../services/streamService";
import { io, Socket } from "socket.io-client";
import { BASE_URL } from "../../../../../services/streamApiClient";

// Derive socket URL from BASE_URL (remove /api suffix)
const SOCKET_URL = BASE_URL.replace("/api", "");

/**
 * Custom hook to manage solo stream state
 * @param username - Username to fetch stream data for
 * @returns Stream data, loading state, and live status
 */
export const useSoloStream = (username: string) => {
  const [streamData, setStreamData] = useState<SoloStreamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const socketRef = useRef<Socket | null>(null);

  // Helper to map API data to SoloStreamData
  const mapToSoloStreamData = (data: PublicStreamData): SoloStreamData => ({
    id: data.id,
    username: data.username, // Display name
    avatarUrl:
      data.avatarUrl || "https://ui-avatars.com/api/?name=" + data.username,
    title: data.title,
    game: "Just Chatting", // Default or fetch if available
    gameIcon: "",
    streamUrl: data.streamUrl || "",
    isLive: data.status === "live",
    viewerCount: data.viewerCount || 0,
    likes: 0, // Need separate API/Socket for likes if needed
    sponsors: [], // Need separate API for sponsors if dynamic
    streamStatus: data.status === "live" ? "live" : "offline",
    platform: data.platform || "youtube", // Default to youtube if missing
  });

  // Fetch initial status
  useEffect(() => {
    let mounted = true;

    const fetchStatus = async () => {
      setLoading(true);
      try {
        const data = await streamService.getStreamerPublicStatus(username);
        if (mounted && data) {
          // If live, data is PublicStreamData. If offline, it might be partial.
          // The backend returns PublicStreamData if live.
          // If offline, it returns { status: 'offline', ... }.
          // We need to handle mapping carefully.

          if (data.status === "live") {
            const mapped = mapToSoloStreamData(data);
            setStreamData(mapped);
            setIsLive(true);
            setViewerCount(data.viewerCount || 0);
          } else {
            // Offline or other status
            setStreamData((prev) =>
              prev ? { ...prev, isLive: false, streamStatus: "offline" } : null
            );
            setIsLive(false);
          }
        } else if (mounted) {
          // No data found / user not streamer
          setStreamData(null);
          setIsLive(false);
        }
      } catch (err) {
        console.error("Error fetching solo stream:", err);
        // Fallback to offline state on error
        setStreamData({
          id: "offline",
          username: username,
          avatarUrl: "https://ui-avatars.com/api/?name=" + username,
          title: "Stream Offline",
          game: "",
          gameIcon: "",
          streamUrl: "",
          isLive: false,
          viewerCount: 0,
          likes: 0,
          sponsors: [],
          streamStatus: "offline",
          platform: "youtube",
        });
        setIsLive(false);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchStatus();

    return () => {
      mounted = false;
    };
  }, [username]);

  // Socket subscription
  useEffect(() => {
    if (!username) return;

    // Connect to stream-room namespace
    const socket = io(`${SOCKET_URL}/stream-room`, {
      transports: ["websocket"],
      autoConnect: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      // Join Home Page updates to catch "NEW_LIVE" for this user
      // We can use the same generic room for now as backend broadcasts widely
      socket.emit("join-home-updates");

      // If we implement specific room joining later:
      // socket.emit('join-stream-room', { streamId: ... });
    });

    const handleStreamListUpdate = (data: any) => {
      // Check if update is for THIS user
      if (data.username === username || data.userId === username) {
        if (data.type === "NEW_LIVE") {
          // User went live!
          // We might need to fetch full details again to be safe, or map data if complete
          // Data from socket might be incomplete, let's trigger a refetch or map what we have
          // For now, let's refetch to get clean PublicStreamData
          streamService.getStreamerPublicStatus(username).then((data) => {
            if (data && data.status === "live") {
              setStreamData(mapToSoloStreamData(data));
              setIsLive(true);
            }
          });
        } else if (data.type === "STREAM_ENDED") {
          setIsLive(false);
          setStreamData((prev) =>
            prev ? { ...prev, isLive: false, streamStatus: "offline" } : null
          );
        }
      }
    };

    /*
    const handleStreamStatus = (data: any) => {
         // Handle status changes (paused, etc) or viewer counts if broadcasted here
         if (data.type === 'STATUS_CHANGED') {
             // update status
         }
    };
    */

    socket.on("stream-list-update", handleStreamListUpdate);
    // socket.on('stream-status-update', handleStreamStatus);

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [username]);

  return {
    streamData,
    loading,
    isLive,
    viewerCount,
    socket: socketRef.current,
  };
};
